import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, ChevronRight, Trash2, Copy, AlertTriangle, Search, Sparkles } from 'lucide-react';
import { ProTopBar } from '../components/ProTopBar';
import { GeneraDietaModal } from '../components/GeneraDietaModal';
import {
  useDietPlans,
  useDeleteDietPlan,
  useCloneDietPlan,
  usePatients,
} from '../lib/queries';
import { ApiError, type DietPlanRow } from '../lib/api';
import styles from './Dashboard.module.css';

type PlanStatus = 'attivo' | 'futuro' | 'concluso';

function computeStatus(plan: DietPlanRow): PlanStatus {
  const today = new Date().toISOString().split('T')[0];
  if (plan.end_date < today) return 'concluso';
  if (plan.start_date > today) return 'futuro';
  return 'attivo';
}

function formatDateIT(iso: string): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

/**
 * Sprint 9.B — Lista piani alimentari del pro.
 * Filtri: search testo (titolo/paziente) + filter status (attivo/futuro/concluso).
 * Azioni per riga: modifica (futuro), clone, elimina.
 */
export default function Piani() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: plans, isLoading, error: loadError } = useDietPlans();
  const { data: patients } = usePatients();
  const deleteMutation = useDeleteDietPlan();
  const cloneMutation = useCloneDietPlan();

  const [searchQ, setSearchQ] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | PlanStatus>('all');
  // Deep-link `?patient=<id>` (es. "Apri piano" dal dettaglio cliente in /clienti)
  // pre-seleziona il filtro paziente così il pro vede subito i suoi piani.
  const [filterPatient, setFilterPatient] = useState<string>(
    () => searchParams.get('patient') || 'all'
  );
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [cloneSource, setCloneSource] = useState<DietPlanRow | null>(null);
  const [cloneTargetId, setCloneTargetId] = useState<string>('');
  const [cloneTitle, setCloneTitle] = useState<string>('');
  const [cloneStartDate, setCloneStartDate] = useState<string>(() =>
    new Date().toISOString().split('T')[0]
  );
  const [actionError, setActionError] = useState<string | null>(null);
  const [showGeneraModal, setShowGeneraModal] = useState(false);

  const filtered = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    return (plans || []).filter((p) => {
      if (filterStatus !== 'all' && computeStatus(p) !== filterStatus) return false;
      if (filterPatient !== 'all' && p.patient_id !== filterPatient) return false;
      if (q) {
        const hay = `${p.title} ${p.patient_name || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [plans, searchQ, filterStatus, filterPatient]);

  const stats = useMemo(() => {
    const list = plans || [];
    return {
      total: list.length,
      attivi: list.filter((p) => computeStatus(p) === 'attivo').length,
      futuri: list.filter((p) => computeStatus(p) === 'futuro').length,
      conclusi: list.filter((p) => computeStatus(p) === 'concluso').length,
    };
  }, [plans]);

  const handleDelete = (planId: string) => {
    setActionError(null);
    void (async () => {
      try {
        await deleteMutation.mutateAsync(planId);
        setConfirmDeleteId(null);
      } catch (err: unknown) {
        const e = err as ApiError | Error;
        setActionError(
          e instanceof ApiError && e.details ? `${e.message} — ${e.details}` : e.message || String(err)
        );
      }
    })();
  };

  const openClone = (plan: DietPlanRow) => {
    setCloneSource(plan);
    setCloneTargetId(plan.patient_id);
    setCloneTitle(`${plan.title} (copia)`);
    setCloneStartDate(new Date().toISOString().split('T')[0]);
    setActionError(null);
  };

  const handleClone = () => {
    if (!cloneSource || !cloneTargetId) return;
    setActionError(null);
    void (async () => {
      try {
        await cloneMutation.mutateAsync({
          sourceId: cloneSource.id,
          body: {
            patient_id: cloneTargetId,
            title: cloneTitle || undefined,
            start_date: cloneStartDate || undefined,
          },
        });
        setCloneSource(null);
      } catch (err: unknown) {
        const e = err as ApiError | Error;
        setActionError(
          e instanceof ApiError && e.details ? `${e.message} — ${e.details}` : e.message || String(err)
        );
      }
    })();
  };

  return (
    <>
      <ProTopBar customCtaLabel="Nuovo piano" onCta={() => navigate('/piani/new')} />

      <div style={{ padding: '4px 0 18px' }}>
        <div
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: 1.6,
            color: 'var(--mute)',
            textTransform: 'uppercase',
          }}
        >
          Studio Pro · Catalogo
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <h1
            style={{
              fontFamily: 'var(--display)',
              fontWeight: 800,
              fontSize: 32,
              letterSpacing: -1,
              margin: '6px 0 0',
              lineHeight: 1.05,
            }}
          >
            Piani alimentari
          </h1>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* CTA primaria: Genera con AI */}
            <button
              type="button"
              onClick={() => setShowGeneraModal(true)}
              style={{
                border: 0,
                background: 'linear-gradient(135deg, #6b46c1, #d946ef)',
                color: '#fff',
                padding: '8px 14px',
                borderRadius: 999,
                fontWeight: 700,
                fontSize: 12,
                cursor: 'pointer',
                display: 'inline-flex',
                gap: 6,
                alignItems: 'center',
              }}
            >
              <Sparkles size={13} /> Genera con AI
            </button>
            <button
              type="button"
              onClick={() => navigate('/piani/templates')}
              style={{
                border: '1px solid var(--line)',
                background: 'var(--paper)',
                color: 'var(--ink)',
                padding: '8px 14px',
                borderRadius: 999,
                fontWeight: 700,
                fontSize: 12,
                cursor: 'pointer',
                display: 'inline-flex',
                gap: 6,
                alignItems: 'center',
              }}
            >
              Template piano →
            </button>
          </div>
        </div>
      </div>

      <div className={styles.kpis}>
        <div className={styles.kpi}>
          <div className={styles.kpiLabel}>Totali</div>
          <div className={`${styles.kpiVal} tnum`}>{stats.total}</div>
        </div>
        <div className={styles.kpi}>
          <div className={styles.kpiLabel}>Attivi</div>
          <div className={`${styles.kpiVal} tnum`}>{stats.attivi}</div>
        </div>
        <div className={styles.kpi}>
          <div className={styles.kpiLabel}>Futuri</div>
          <div className={`${styles.kpiVal} tnum`}>{stats.futuri}</div>
        </div>
        <div className={styles.kpi}>
          <div className={styles.kpiLabel}>Conclusi</div>
          <div className={`${styles.kpiVal} tnum`}>{stats.conclusi}</div>
        </div>
      </div>

      <section className={styles.card} style={{ padding: 16, marginBottom: 14 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 10,
            alignItems: 'end',
          }}
        >
          <div style={{ position: 'relative' }}>
            <Search
              size={14}
              style={{ position: 'absolute', top: 11, left: 10, color: 'var(--mute)', pointerEvents: 'none' }}
            />
            <input
              type="text"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              placeholder="Cerca per titolo o paziente…"
              style={{
                width: '100%',
                border: '1px solid var(--line)',
                borderRadius: 6,
                padding: '8px 10px 8px 30px',
                fontSize: 13,
                background: 'var(--surface)',
                color: 'var(--ink)',
                fontFamily: 'var(--sans)',
                boxSizing: 'border-box',
              }}
            />
          </div>
          <div>
            <label
              style={{
                display: 'block',
                fontFamily: 'var(--mono)',
                fontSize: 10,
                fontWeight: 700,
                color: 'var(--mute)',
                letterSpacing: 1.2,
                textTransform: 'uppercase',
                marginBottom: 4,
              }}
            >
              Status
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as 'all' | PlanStatus)}
              style={{
                width: '100%',
                border: '1px solid var(--line)',
                borderRadius: 6,
                padding: '8px 10px',
                fontSize: 13,
                background: 'var(--surface)',
                color: 'var(--ink)',
                boxSizing: 'border-box',
              }}
            >
              <option value="all">Tutti</option>
              <option value="attivo">Attivi</option>
              <option value="futuro">Futuri</option>
              <option value="concluso">Conclusi</option>
            </select>
          </div>
          <div>
            <label
              style={{
                display: 'block',
                fontFamily: 'var(--mono)',
                fontSize: 10,
                fontWeight: 700,
                color: 'var(--mute)',
                letterSpacing: 1.2,
                textTransform: 'uppercase',
                marginBottom: 4,
              }}
            >
              Paziente
            </label>
            <select
              value={filterPatient}
              onChange={(e) => setFilterPatient(e.target.value)}
              style={{
                width: '100%',
                border: '1px solid var(--line)',
                borderRadius: 6,
                padding: '8px 10px',
                fontSize: 13,
                background: 'var(--surface)',
                color: 'var(--ink)',
                boxSizing: 'border-box',
              }}
            >
              <option value="all">Tutti</option>
              {(patients || []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      <section className={styles.card}>
        {isLoading ? (
          <div className={styles.empty}>Caricamento piani…</div>
        ) : loadError ? (
          <div className={styles.empty} style={{ color: 'var(--red)' }}>
            Errore: {(loadError as Error).message}
          </div>
        ) : !plans || plans.length === 0 ? (
          <div className={styles.empty} style={{ padding: '40px 24px' }}>
            <div style={{ marginBottom: 12, fontSize: 14 }}>
              Nessun piano creato. Inizia da "Nuovo piano".
            </div>
            <button
              type="button"
              onClick={() => navigate('/piani/new')}
              style={{
                padding: '10px 18px',
                background: 'var(--ink)',
                color: '#fff',
                border: 0,
                borderRadius: 8,
                fontWeight: 700,
                fontSize: 13,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <Plus size={14} /> Nuovo piano
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className={styles.empty}>Nessun piano corrisponde ai filtri.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--surface-2)' }}>
                  <th style={th}>Titolo</th>
                  <th style={th}>Paziente</th>
                  <th style={th}>Periodo</th>
                  <th style={th}>Pasti</th>
                  <th style={th}>Status</th>
                  <th style={th}>In uso</th>
                  <th style={{ ...th, textAlign: 'right' }}>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const status = computeStatus(p);
                  const statusColor =
                    status === 'attivo'
                      ? 'var(--green-deep)'
                      : status === 'futuro'
                      ? 'var(--orange-deep, #a87c00)'
                      : 'var(--mute)';
                  return (
                    <tr key={p.id}>
                      <td style={td}>
                        <button
                          type="button"
                          onClick={() => navigate(`/piani/${p.id}/edit`)}
                          style={{
                            border: 0,
                            background: 'transparent',
                            color: 'var(--ink)',
                            fontWeight: 600,
                            cursor: 'pointer',
                            padding: 0,
                            fontSize: 13,
                            textAlign: 'left',
                          }}
                        >
                          {p.title}
                        </button>
                      </td>
                      <td style={td}>{p.patient_name || '—'}</td>
                      <td style={td}>
                        {formatDateIT(p.start_date)} → {formatDateIT(p.end_date)}
                      </td>
                      <td style={td}>{p.meal_count}</td>
                      <td style={td}>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: 4,
                            fontSize: 11,
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            color: '#fff',
                            background: statusColor,
                          }}
                        >
                          {status}
                        </span>
                      </td>
                      <td style={td}>
                        {p.status === 'active' ? (
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              borderRadius: 4,
                              fontSize: 11,
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: 0.5,
                              color: '#fff',
                              background: 'var(--green-deep, #0b8a3e)',
                            }}
                          >
                            In uso
                          </span>
                        ) : p.status === 'archived' ? (
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              borderRadius: 4,
                              fontSize: 11,
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: 0.5,
                              color: 'var(--mute)',
                              background: 'var(--surface-2)',
                              border: '1px solid var(--line)',
                            }}
                          >
                            Archiviato
                          </span>
                        ) : (
                          <span style={{ color: 'var(--mute)', fontSize: 12 }}>—</span>
                        )}
                      </td>
                      <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <button
                          type="button"
                          onClick={() => openClone(p)}
                          aria-label="Clona piano"
                          title="Clona piano"
                          style={rowAction}
                        >
                          <Copy size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDeleteId(p.id)}
                          aria-label="Elimina piano"
                          title="Elimina piano"
                          style={{ ...rowAction, color: 'var(--red)' }}
                        >
                          <Trash2 size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => navigate(`/piani/${p.id}/edit`)}
                          aria-label="Apri piano"
                          title="Apri piano"
                          style={rowAction}
                        >
                          <ChevronRight size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Confirm delete modal */}
      {confirmDeleteId && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => !deleteMutation.isPending && setConfirmDeleteId(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--surface)',
              borderRadius: 12,
              padding: 22,
              maxWidth: 440,
              boxShadow: '0 20px 60px rgba(0,0,0,0.32)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <AlertTriangle size={20} color="var(--red)" />
              <h3 style={{ fontFamily: 'var(--display)', fontSize: 17, fontWeight: 700, margin: 0 }}>
                Eliminare piano?
              </h3>
            </div>
            <p style={{ color: 'var(--ink-2)', fontSize: 13.5, lineHeight: 1.5, margin: '0 0 14px' }}>
              Il piano e i suoi pasti/ingredienti/liste della spesa verranno eliminati definitivamente. L'azione
              non è reversibile.
            </p>
            {actionError && (
              <div
                style={{
                  background: 'var(--red-soft, #fff0f0)',
                  border: '1px solid var(--red)',
                  borderRadius: 6,
                  padding: 10,
                  fontSize: 12.5,
                  color: 'var(--red)',
                  marginBottom: 12,
                }}
              >
                {actionError}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                onClick={() => setConfirmDeleteId(null)}
                disabled={deleteMutation.isPending}
                style={{
                  border: '1px solid var(--line)',
                  background: 'var(--surface)',
                  color: 'var(--ink-2)',
                  padding: '8px 14px',
                  borderRadius: 8,
                  fontWeight: 600,
                  cursor: deleteMutation.isPending ? 'not-allowed' : 'pointer',
                }}
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={() => handleDelete(confirmDeleteId)}
                disabled={deleteMutation.isPending}
                style={{
                  border: 0,
                  background: 'var(--red)',
                  color: '#fff',
                  padding: '8px 14px',
                  borderRadius: 8,
                  fontWeight: 700,
                  cursor: deleteMutation.isPending ? 'not-allowed' : 'pointer',
                }}
              >
                {deleteMutation.isPending ? 'Eliminazione…' : 'Elimina'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal genera dieta con AI */}
      {showGeneraModal && (
        <GeneraDietaModal onClose={() => setShowGeneraModal(false)} />
      )}

      {/* Clone modal */}
      {cloneSource && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => !cloneMutation.isPending && setCloneSource(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--surface)',
              borderRadius: 12,
              padding: 22,
              maxWidth: 460,
              width: '100%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.32)',
            }}
          >
            <h3 style={{ fontFamily: 'var(--display)', fontSize: 17, fontWeight: 700, margin: '0 0 12px' }}>
              Clona "{cloneSource.title}"
            </h3>

            <FieldBlock label="Paziente target">
              <select
                value={cloneTargetId}
                onChange={(e) => setCloneTargetId(e.target.value)}
                style={inputStyle}
              >
                {(patients || []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </FieldBlock>

            <div style={{ marginTop: 10 }}>
              <FieldBlock label="Titolo nuovo piano">
                <input
                  type="text"
                  value={cloneTitle}
                  onChange={(e) => setCloneTitle(e.target.value)}
                  style={inputStyle}
                />
              </FieldBlock>
            </div>

            <div style={{ marginTop: 10 }}>
              <FieldBlock label="Data inizio">
                <input
                  type="date"
                  value={cloneStartDate}
                  onChange={(e) => setCloneStartDate(e.target.value)}
                  style={inputStyle}
                />
              </FieldBlock>
            </div>

            {actionError && (
              <div
                style={{
                  background: 'var(--red-soft, #fff0f0)',
                  border: '1px solid var(--red)',
                  borderRadius: 6,
                  padding: 10,
                  fontSize: 12.5,
                  color: 'var(--red)',
                  marginTop: 12,
                }}
              >
                {actionError}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button
                type="button"
                onClick={() => setCloneSource(null)}
                disabled={cloneMutation.isPending}
                style={{
                  border: '1px solid var(--line)',
                  background: 'var(--surface)',
                  color: 'var(--ink-2)',
                  padding: '8px 14px',
                  borderRadius: 8,
                  fontWeight: 600,
                  cursor: cloneMutation.isPending ? 'not-allowed' : 'pointer',
                }}
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={handleClone}
                disabled={cloneMutation.isPending || !cloneTargetId}
                style={{
                  border: 0,
                  background: 'var(--green-deep)',
                  color: '#fff',
                  padding: '8px 14px',
                  borderRadius: 8,
                  fontWeight: 700,
                  cursor: cloneMutation.isPending || !cloneTargetId ? 'not-allowed' : 'pointer',
                }}
              >
                {cloneMutation.isPending ? 'Clonazione…' : 'Clona'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const th: React.CSSProperties = {
  padding: '10px 12px',
  textAlign: 'left',
  fontFamily: 'var(--mono)',
  fontSize: 10.5,
  fontWeight: 700,
  color: 'var(--mute)',
  letterSpacing: 1.2,
  textTransform: 'uppercase',
  borderBottom: '1px solid var(--line-3)',
};

const td: React.CSSProperties = {
  padding: '10px 12px',
  borderBottom: '1px solid var(--line-3)',
  fontSize: 13,
  color: 'var(--ink)',
};

const rowAction: React.CSSProperties = {
  border: '1px solid var(--line)',
  background: 'var(--surface)',
  color: 'var(--ink-2)',
  padding: '6px 8px',
  borderRadius: 6,
  cursor: 'pointer',
  marginLeft: 4,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  border: '1px solid var(--line)',
  borderRadius: 6,
  padding: '8px 10px',
  fontSize: 13,
  background: 'var(--surface)',
  color: 'var(--ink)',
  fontFamily: 'var(--sans)',
  boxSizing: 'border-box',
};

function FieldBlock({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <span
        style={{
          fontFamily: 'var(--mono)',
          fontSize: 10.5,
          fontWeight: 700,
          color: 'var(--mute)',
          letterSpacing: 1.2,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}
